import { type FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Ellipsis, Home, Search, Settings } from "lucide-react";

import { useDemoMode, useNavigateToDemoEstate } from "@/pages/demo/DemoModeContext";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/components/ui/sidebar";
import { useLawyerEstateNav } from "@/shared/hooks/useLawyerEstates";
import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import {
  AUTH_ROUTES,
  AUTH_QUERY_PARAMS,
  AUTH_SOURCES,
  LAWYER_ROUTES,
  DEMO_ROUTES,
} from "@/shared/constants/paths";
import { clearCachedLawyerStatus } from "@/shared/hooks/useIsLawyer";
import { cn } from "@/shared/lib/utils";
import logoAttorney from "@/shared/assets/logo/logo-attorney.svg";

import { SidebarUserFooter } from "./SidebarUserFooter";

export const LawyerSidebar: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDemoMode, selectedDemoEstateId } = useDemoMode();
  const navigateToDemoEstate = useNavigateToDemoEstate();
  const { data: estates } = useLawyerEstateNav();
  const [estatesOpen, setEstatesOpen] = useState(true);
  const isDashboard = isDemoMode
    ? location.pathname === DEMO_ROUTES.LAWYER
    : location.pathname === LAWYER_ROUTES.DASHBOARD;
  const isSettings = !isDemoMode && location.pathname === LAWYER_ROUTES.SETTINGS;
  const isClientDetail = isDemoMode
    ? location.pathname === DEMO_ROUTES.LAWYER_CLIENT
    : location.pathname.startsWith("/lawyer/client/");
  const activeEstateId = isDemoMode
    ? isClientDetail
      ? String(selectedDemoEstateId)
      : null
    : isClientDetail
      ? (location.pathname.match(/\/lawyer\/client\/(\d+)/)?.[1] ?? null)
      : null;

  const handleLogout = () => {
    clearCachedLawyerStatus();
    window.location.href = `${AUTH_ROUTES.LOGOUT}?${AUTH_QUERY_PARAMS.FROM}=${AUTH_SOURCES.LAWYER}`;
  };

  return (
    <Sidebar
      className={cn("border-r border-primary", isDemoMode && "top-16 h-[calc(100svh-4rem)]")}
    >
      <SidebarHeader className="px-3 pt-4 pb-4">
        <div className="flex items-start gap-3">
          <img src={logoAttorney} alt="Elayne for Attorneys" className="h-[54px]" />
          {isDemoMode && (
            <Tag color="stone" size="sm" className="mt-1 shrink-0">
              Demo
            </Tag>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-1 overflow-y-auto">
        <SidebarGroup className="px-2 py-0">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Estate Matters"
                onClick={() => {
                  if (isDashboard) {
                    setEstatesOpen((prev) => !prev);
                  } else {
                    navigate(isDemoMode ? DEMO_ROUTES.LAWYER : LAWYER_ROUTES.DASHBOARD);
                    setEstatesOpen(true);
                  }
                }}
                className={cn(
                  "flex-1 text-primary hover:bg-subtle [&>svg]:size-4",
                  isDashboard && "bg-subtle font-medium",
                )}
              >
                <Home size={16} />
                <span className="flex-1">Estate Matters</span>
                {estates && estates.length > 0 && (
                  <ChevronDown
                    size={16}
                    className={cn("transition-transform", !estatesOpen && "-rotate-90")}
                  />
                )}
              </SidebarMenuButton>

              {estatesOpen && estates && estates.length > 0 && (
                <div className="relative ml-6 mt-1 border-l border-primary pl-3">
                  {estates.map((estate) => (
                    <button
                      key={estate.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDemoMode) {
                          navigateToDemoEstate(estate.estateId);
                        } else {
                          navigate(
                            LAWYER_ROUTES.CLIENT_DETAIL.replace(":userId", String(estate.estateId)),
                          );
                        }
                      }}
                      className={cn(
                        "block w-full truncate rounded-md px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-subtle",
                        activeEstateId === String(estate.estateId) && "bg-subtle font-medium",
                      )}
                    >
                      {estate.decedentName ?? "Unknown"}
                    </button>
                  ))}
                </div>
              )}
            </SidebarMenuItem>

            <SidebarMenuItem className="flex items-center gap-2">
              <SidebarMenuButton
                tooltip="Insights"
                disabled
                className="flex-1 text-primary [&>svg]:size-4"
              >
                <Search size={16} />
                <span className="flex-1">Insights</span>
              </SidebarMenuButton>
              <Tag color="green" size="sm" className="shrink-0">
                Coming soon
              </Tag>
            </SidebarMenuItem>

            {!isDemoMode && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Settings"
                  onClick={() => navigate(LAWYER_ROUTES.SETTINGS)}
                  className={cn(
                    "flex-1 text-primary hover:bg-subtle [&>svg]:size-4",
                    isSettings && "bg-subtle font-medium",
                  )}
                >
                  <Settings size={16} />
                  <span className="flex-1">Settings</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="px-3 pb-4 flex flex-col gap-4">
        {isDemoMode && (
          <div className="rounded-lg border border-primary bg-subtle p-4 flex flex-col gap-2">
            <p className="text-sm font-semibold text-primary">
              Your first client's IRS transcripts on us
            </p>
            <Button variant="brand" className="mt-1" onClick={() => navigate(LAWYER_ROUTES.SIGNUP)}>
              Create account
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        <SidebarUserFooter
          trigger={
            <button
              className="flex items-center justify-center size-8 rounded-md hover:bg-subtle transition-colors outline-none focus-visible:outline-none"
              aria-label="Account menu"
            >
              <Ellipsis className="size-4 text-secondary" />
            </button>
          }
          onLogout={handleLogout}
        />
      </SidebarFooter>
    </Sidebar>
  );
};
